import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { FREEMIUM_LIMITS, SKIP_AUTH_FOR_DEV } from '../lib/config';

const AuthContext = createContext(null);

const defaultUsage = {
  searches_made: 0,
  emails_fetched: 0,
  emails_drafted: 0,
  emails_sent: 0,
};

const AuthProvider = ({ children }) => {
  const [isSignedIn, setIsSignedIn] = useState(SKIP_AUTH_FOR_DEV);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [userBio, setUserBio] = useState('');
  const [userUsageStats, setUserUsageStats] = useState(defaultUsage);
  const [saving, setSaving] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');

  const openEmailAuthModal = useCallback(() => setShowEmailAuth(true), []);
  const closeEmailAuthModal = useCallback(() => setShowEmailAuth(false), []);

  const handleGoogleAuth = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: 'email profile openid https://www.googleapis.com/auth/gmail.send',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
        redirectTo: window.location.origin,
      },
    });
  }, []);

  const handleSkipAuth = useCallback(async () => {
    if (isSignedIn) return;
    try {
      const testUserId = 'bd44b0b1-f74c-4c5c-96ea-121f58bdd86f';
      const testEmail = 'montysage@gmail.com';
      setUserEmail(testEmail);
      setUserId(testUserId);
      setIsSignedIn(true);
      const { data } = await supabase
        .from('users')
        .select('full_name, bio, searches_made, emails_fetched, emails_drafted, emails_sent')
        .eq('id', testUserId)
        .single();
      if (data) {
        setFullName(data.full_name || '');
        setUserBio(data.bio || '');
        setUserUsageStats({
          searches_made: data.searches_made || 0,
          emails_fetched: data.emails_fetched || 0,
          emails_drafted: data.emails_drafted || 0,
          emails_sent: data.emails_sent || 0,
        });
      }
    } catch (error) {
      console.error('Failed to skip auth:', error);
    }
  }, [isSignedIn]);

  const ensureUserRecordExists = useCallback(
    async (id, email) => {
      if (!id) return false;
      try {
        const { error } = await supabase.from('users').select('id').eq('id', id).single();
        if (error && error.code === 'PGRST116') {
          const { error: createError } = await supabase
            .from('users')
            .insert({
              id,
              email: email || userEmail || '',
              full_name: '',
              bio: '',
              searches_made: 0,
              emails_fetched: 0,
              emails_drafted: 0,
              emails_sent: 0,
            });
          if (createError) {
            console.error('Failed to create user record:', createError);
            return false;
          }
          return true;
        }
        if (error) {
          console.error('Error checking user record:', error);
          return false;
        }
        return true;
      } catch (err) {
        console.error('Unexpected error checking user record:', err);
        return false;
      }
    },
    [userEmail],
  );

  const getCurrentUserId = useCallback(async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error || !user) return null;
    return user.id;
  }, []);

  const fetchUserProfile = useCallback(
    async supabaseUserId => {
      const { data, error } = await supabase
        .from('users')
        .select('full_name, bio, searches_made, emails_fetched, emails_drafted, emails_sent')
        .eq('id', supabaseUserId)
        .single();
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user profile:', error);
        return;
      }
      if (data) {
        setFullName(data.full_name || '');
        setUserBio(data.bio || '');
        setUserUsageStats({
          searches_made: data.searches_made || 0,
          emails_fetched: data.emails_fetched || 0,
          emails_drafted: data.emails_drafted || 0,
          emails_sent: data.emails_sent || 0,
        });
      } else if (error && error.code === 'PGRST116') {
        const { error: createError } = await supabase
          .from('users')
          .insert({
            id: supabaseUserId,
            email: userEmail || '',
            full_name: '',
            bio: '',
            ...defaultUsage,
          })
          .select()
          .single();
        if (createError) {
          console.error('Error creating user record:', createError);
        } else {
          setFullName('');
          setUserBio('');
          setUserUsageStats(defaultUsage);
        }
      }
    },
    [userEmail],
  );

  useEffect(() => {
    let mounted = true;
    let authInitialized = false;

    // Initialize auth state
    const initAuth = async () => {
      try {
        console.log('🔐 Initializing auth...');
        setAuthLoading(true);
        
        if (SKIP_AUTH_FOR_DEV) {
          console.log('🔐 Skipping auth for dev');
          try {
            await handleSkipAuth();
          } catch (error) {
            console.error('Error in handleSkipAuth:', error);
          }
          if (mounted) {
            setAuthLoading(false);
            authInitialized = true;
          }
          return;
        }

        // Check for existing session first (more reliable than getUser)
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          if (mounted) {
            setIsSignedIn(false);
            setAuthLoading(false);
            authInitialized = true;
          }
          return;
        }

        if (session?.user) {
          // User is signed in
          console.log('🔐 User session found:', session.user.email);
          setUserEmail(session.user.email || '');
          setUserId(session.user.id);
          setIsSignedIn(true);
          // Don't await fetchUserProfile - let it run in background
          fetchUserProfile(session.user.id).catch(err => {
            console.error('Error fetching user profile:', err);
          });
        } else {
          // No session found
          console.log('🔐 No session found');
          setIsSignedIn(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setIsSignedIn(false);
        }
      } finally {
        if (mounted) {
          console.log('🔐 Auth initialization complete');
          setAuthLoading(false);
          authInitialized = true;
        }
      }
    };

    // Start initialization
    initAuth();

    // Listen for auth state changes (e.g., OAuth redirects, sign out)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('🔐 Auth state change:', event, session?.user?.email);

      try {
        if (event === 'INITIAL_SESSION') {
          // This fires on mount with current session
          // Only set loading to false if we haven't initialized yet
          if (!authInitialized && mounted) {
            setAuthLoading(false);
            authInitialized = true;
          }
        } else if (event === 'SIGNED_IN' && session?.user) {
          setUserEmail(session.user.email || '');
          setUserId(session.user.id);
          setIsSignedIn(true);
          setAuthLoading(false);
          authInitialized = true;
          // Don't await - run in background
          fetchUserProfile(session.user.id).catch(err => {
            console.error('Error fetching user profile:', err);
          });
        } else if (event === 'SIGNED_OUT') {
          setIsSignedIn(false);
          setUserEmail('');
          setUserId(null);
          setFullName('');
          setUserBio('');
          setUserUsageStats(defaultUsage);
          setAuthLoading(false);
          authInitialized = true;
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Update user info on token refresh
          setUserEmail(session.user.email || '');
          setUserId(session.user.id);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        if (mounted) {
          setAuthLoading(false);
          authInitialized = true;
        }
      }
    });

    // Safety timeout - ensure loading completes after 5 seconds max
    const timeoutId = setTimeout(() => {
      if (mounted && !authInitialized) {
        console.warn('⚠️ Auth initialization timeout - forcing completion');
        setAuthLoading(false);
        authInitialized = true;
      }
    }, 5000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserProfile, handleSkipAuth]);

  const saveUserInfo = useCallback(
    async (field, value) => {
      if (!userId) return;
      setSaving(true);
      await supabase.from('users').upsert({ id: userId, email: userEmail, [field]: value });
      setSaving(false);
    },
    [userEmail, userId],
  );

  const logout = useCallback(async () => {
    setIsSignedIn(false);
    setUserEmail('');
    setUserId(null);
    setFullName('');
    setUserBio('');
    setUserUsageStats(defaultUsage);
    setShowEmailAuth(false);
    setShowUpgradeModal(false);
    setUpgradeReason('');
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const handleEmailAuth = useCallback(async () => {
    setShowEmailAuth(false);
  }, []);

  const checkFreemiumLimit = useCallback(
    action => {
      const currentUsage = userUsageStats[action] || 0;
      const limit = FREEMIUM_LIMITS[action];
      if (currentUsage >= limit) {
        const actionNames = {
          searches_made: 'searches',
          emails_fetched: 'email lookups',
          emails_drafted: 'email drafts',
          emails_sent: 'emails sent',
        };
        setUpgradeReason(`You've reached your limit of ${limit} ${actionNames[action]}. Upgrade to continue using Ladder!`);
        setShowUpgradeModal(true);
        return false;
      }
      return true;
    },
    [userUsageStats],
  );

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeReason('');
  }, []);

  const incrementUsage = useCallback(
    async action => {
      if (!userId) return;
      const newValue = (userUsageStats[action] || 0) + 1;
      await supabase.from('users').update({ [action]: newValue }).eq('id', userId);
      setUserUsageStats(prev => ({ ...prev, [action]: newValue }));
    },
    [userId, userUsageStats],
  );

  const value = useMemo(
    () => ({
      isSignedIn,
      userId,
      userEmail,
      fullName,
      setFullName,
      userBio,
      setUserBio,
      userUsageStats,
      saving,
      showEmailAuth,
      authLoading,
      showUpgradeModal,
      upgradeReason,
      handleGoogleAuth,
      handleEmailAuth,
      logout,
      saveUserInfo,
      openEmailAuthModal,
      closeEmailAuthModal,
      checkFreemiumLimit,
      closeUpgradeModal,
      incrementUsage,
      ensureUserRecordExists,
      getCurrentUserId,
    }),
    [
      authLoading,
      checkFreemiumLimit,
      closeEmailAuthModal,
      closeUpgradeModal,
      ensureUserRecordExists,
      fullName,
      handleEmailAuth,
      handleGoogleAuth,
      incrementUsage,
      isSignedIn,
      logout,
      openEmailAuthModal,
      saveUserInfo,
      setFullName,
      setUserBio,
      showEmailAuth,
      showUpgradeModal,
      upgradeReason,
      userBio,
      userEmail,
      userId,
      userUsageStats,
      saving,
      getCurrentUserId,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
